import React, { useRef } from "react";
import classNames from "classnames";
import { Label } from "reactstrap";
import { useField } from "formik";
import { useTranslation } from "react-i18next";

import { noop } from "@helpers/common";
import CustomInput from "../Input";
import EmojiPicker from "../EmojiPicker";
import ErrorMessageDefault from "../../base/components/ErrorMessageDefault";
import { getErrorLabel } from "@helpers/validation";

import styles from "./index.module.scss";

const FormikInput = ({
  placeholder,
  name,
  type,
  maxLength,
  maxLengthForCounter,
  readOnly,
  label,
  min = 0,
  disabled,
  className,
  wrapperClassName,
  prefixClassName,
  labelClassName,
  ErrorMessage = ErrorMessageDefault,
  step,
  mask,
  formatter,
  onChange,
  onBlur,
  pipeValue = (data) => data,
  autoFocus = false,
  PrefixIcon,
  hideError,
  LabelIcon,
  MaxWithdrawalBtn,
  isEmoji,
  setIsOpen,
  isOpen,
  dataCCBill,
  externalErrorMessage,
  children,
  onFocus = noop,
  contentStyles,
  style,
  withoutTouch,
  ...rest
}) => {
  const inputRef = useRef(null);

  const [field, meta, { setValue, setTouched }] = useField({
    name,
    type,
    placeholder,
  });

  const isInvalid =
    (withoutTouch ? meta.error : meta.error && meta.touched) ||
    externalErrorMessage;
  const { t } = useTranslation("labels");
  const { t: t1 } = useTranslation("placeholders");
  const { t: t2 } = useTranslation("errors");

  const errorLabel = getErrorLabel(label);

  const handleChange = ({ target }) => {
    setTouched(true);

    // find way to avoid this
    const value =
      name === "name" ? target.value.replace(/  +/g, "") : target.value;
    if (onChange) {
      onChange(value);
    }

    if (formatter) {
      setValue(value, true);
      return;
    }

    setValue(value);
  };

  const handleBlur = (event) => {
    onBlur?.(event);
    field.onBlur(event);
  };

  const checkIsErrorMessageVisible = () => {
    if (withoutTouch) {
      return meta.error && !hideError;
    }
    return meta.touched && meta.error && !hideError;
  };

  return (
    <section className={classNames("form-control-wrapper", wrapperClassName)}>
      {!LabelIcon ? (
        label && (
          <Label
            for={name}
            className={classNames("text-body-2", labelClassName, { disabled })}
          >
            {t(label)}
          </Label>
        )
      ) : (
        <Label for={name}>
          <LabelIcon />
        </Label>
      )}
      <div className="position-relative w-100">
        {PrefixIcon && (
          <div
            className={classNames(
              "position-absolute",
              disabled && "text-gray",
              prefixClassName,
              styles.prefixIcon,
            )}
          >
            <PrefixIcon className="text-primary" />
          </div>
        )}
        <CustomInput
          {...field}
          style={style}
          ref={inputRef}
          onFocus={onFocus}
          onBlur={handleBlur}
          id={name}
          value={pipeValue(field.value)}
          onChange={handleChange}
          type={type}
          className={classNames({
            [className]: className,
            invalid: isInvalid,
            withPrefix: !!PrefixIcon,
          })}
          maxLength={maxLength}
          readOnly={readOnly}
          disabled={disabled}
          placeholder={t1(placeholder)}
          step={step}
          mask={mask}
          autoFocus={autoFocus}
          isError={isInvalid}
          dataCCBill={dataCCBill}
          {...rest}
        />
        {isEmoji && (
          <EmojiPicker
            inputRef={inputRef}
            emojiClassName={styles.textfieldEmoji}
            value={field.value}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            setValue={(value) => {
              setValue(value, true);
              setTouched(true);
            }}
          />
        )}
        {MaxWithdrawalBtn ? <MaxWithdrawalBtn /> : null}
      </div>
      <div className={contentStyles}>
        {children}
        {(externalErrorMessage || checkIsErrorMessageVisible()) && (
          <ErrorMessage className={classNames(styles.error)} name={name}>
            {t2(externalErrorMessage ? externalErrorMessage : meta.error, {
              label: t(errorLabel),
              min,
              max: maxLengthForCounter ? maxLengthForCounter : maxLength,
              labelLowerCased: t(label)?.toLowerCase(),
            })}
          </ErrorMessage>
        )}
      </div>
    </section>
  );
};

export default FormikInput;
